import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Loader2, Plus, X } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { CrumbBar } from "@/components/layout/Crumbs";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { GreenButton } from "@/components/Buttons";
import { uploadArtwork } from "@/components/signs/useSignConfig";

const inputCls = "w-full rounded-xl border border-white/15 bg-brand-ink px-4 py-3 text-sm text-white placeholder:text-zinc-500 focus:border-brand-green focus:outline-none";
const KINDS = [
    { key: "machine", label: "Machine" },
    { key: "supply", label: "Supply" },
    { key: "blank", label: "Blanks" },
    { key: "apparel", label: "Apparel" },
    { key: "class", label: "Class" },
    { key: "wanted", label: "Wanted ad" },
];
const CONDITIONS = ["new", "like-new", "used", "for-parts"];

const PLACEHOLDERS = {
    machine: "Title — e.g. Brother GT381 DTG printer",
    supply: "Title — e.g. 42cm DTF film, 100m roll",
    blank: "Title — e.g. Gildan 5000 white blanks, case of 72",
    apparel: "Title — e.g. My streetwear brand — heavyweight white tees",
    class: "Class name — e.g. Screen printing 101 in Bushwick",
    wanted: "Title — e.g. Wanted: used 24\" conveyor dryer",
};

export default function SellListing() {
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    const startKind = KINDS.some((k) => k.key === sp.get("kind")) ? sp.get("kind") : "machine";
    const [form, setForm] = useState({
        kind: startKind, title: "", description: "", price: "", accept_offers: true,
        condition: startKind === "class" ? "new" : "used", brand: "", quantity: 1, unit: "",
        location: "", images: [], apparel_group: sp.get("group") === "colored" ? "colored" : "white",
        duration: "", capacity: "",
    });
    const [busy, setBusy] = useState(false);
    const [locked, setLocked] = useState(null);
    const [uploading, setUploading] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const isClass = form.kind === "class";
    const isApparel = form.kind === "apparel";

    const addPhotos = async (files) => {
        if (!files?.length) return;
        setUploading(true);
        try {
            const recs = await Promise.all([...files].slice(0, 6).map((f) => uploadArtwork(f)));
            setForm((f) => ({ ...f, images: [...f.images, ...recs.map((r) => r.url)].slice(0, 6) }));
        } catch (e) { toast.error("Photo upload failed"); }
        finally { setUploading(false); }
    };

    const submit = async () => {
        if (!form.title.trim()) { toast.error("Give your listing a title"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/marketplace/listings", {
                ...form,
                price: form.kind === "wanted" || form.price === "" ? null : Number(form.price),
                quantity: Number(form.quantity) || 1,
                condition: isClass ? "new" : form.condition,
                brand: form.brand || null,
                unit: isClass ? "person" : form.unit || null,
                apparel_group: isApparel ? form.apparel_group : null,
                duration: isClass ? form.duration || null : null,
                capacity: isClass && form.capacity ? Number(form.capacity) : null,
                tags: [],
            });
            toast.success(data.note || "Listing submitted");
            navigate("/account/listings");
        } catch (e) {
            const detail = formatApiError(e.response?.data?.detail);
            if (e.response?.status === 402) { setLocked(detail); }
            else { toast.error(detail); }
        }
        finally { setBusy(false); }
    };

    return (
        <Layout hideTicker>

            <CrumbBar trail={[{ label: "Classifieds", to: "/supplies" }, { label: "Post an ad" }]} />
            <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
                <Link to="/supplies" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-brand-green"><ArrowLeft size={15} /> Marketplace</Link>
                <h1 className="mt-4 font-display text-3xl font-black tracking-tight sm:text-4xl" data-testid="sell-title">Sell on 13thav</h1>
                <p className="mt-2 text-zinc-400">
                    Machines, supplies, blanks, your apparel brand or a class you teach. Free to list — we check new
                    listings before they go live, usually within a day.
                </p>

                {locked && (
                    <div className="mt-6 rounded-3xl border border-brand-green/40 bg-brand-green/[0.08] p-6" data-testid="upgrade-prompt">
                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-green">Unlock more space</p>
                        <h2 className="mt-2 font-display text-2xl font-black tracking-tight">{locked}</h2>
                        <p className="mt-2 text-sm text-zinc-300">
                            Storefront is $20/mo (or $100/yr) — list on every page, 40 listings, your own storefront page.
                            Department Warehouse is $50/mo for unlimited listings and your own designer. 30 days free either way.
                        </p>
                        <div className="mt-4 flex flex-wrap gap-3">
                            <Link to="/directory"><GreenButton data-testid="upgrade-cta">See plans</GreenButton></Link>
                            <button onClick={() => setLocked(null)} className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white" data-testid="upgrade-dismiss">not now</button>
                        </div>
                    </div>
                )}

                <div className="mt-8 space-y-5 rounded-3xl border border-white/10 bg-white/[0.03] p-6">
                    <div className="flex flex-wrap gap-2" data-testid="sell-kind">
                        {KINDS.map((k) => (
                            <button key={k.key} type="button" onClick={() => setForm((f) => ({ ...f, kind: k.key }))}
                                data-testid={`sell-kind-${k.key}`}
                                className={`rounded-full border px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors ${form.kind === k.key ? "border-brand-green bg-brand-green text-black" : "border-white/12 text-zinc-300 hover:border-white/35"}`}>
                                {k.label}
                            </button>
                        ))}
                    </div>

                    <input className={inputCls} placeholder={PLACEHOLDERS[form.kind]} value={form.title} onChange={set("title")} data-testid="sell-title-input" />
                    <textarea className={inputCls} rows={6}
                        placeholder={isClass
                            ? "What students will make and learn, skill level, what's included, where you teach…"
                            : "Condition, hours, what's included, sizes, why you're selling, pickup or shipping…"}
                        value={form.description} onChange={set("description")} data-testid="sell-description" />

                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <p className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                                {isClass ? "Price per person (USD)" : "Price (USD)"}
                            </p>
                            <input className={inputCls} type="number" min="0" step="1" placeholder={form.kind === "wanted" ? "Leave blank" : isClass ? "99" : "9400"} value={form.price} onChange={set("price")} data-testid="sell-price" />
                        </div>
                        {isClass ? (
                            <div>
                                <p className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Length</p>
                                <input className={inputCls} placeholder="2 hours" value={form.duration} onChange={set("duration")} data-testid="sell-duration" />
                            </div>
                        ) : (
                            <div>
                                <p className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Condition</p>
                                <select className={inputCls} value={form.condition} onChange={set("condition")} data-testid="sell-condition">
                                    {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                        )}
                        {isApparel && (
                            <div>
                                <p className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Apparel section</p>
                                <select className={inputCls} value={form.apparel_group} onChange={set("apparel_group")} data-testid="sell-apparel-group">
                                    <option value="white">White apparel</option>
                                    <option value="colored">Colored apparel</option>
                                </select>
                            </div>
                        )}
                        <div>
                            <p className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                                {isClass ? "Teaching as (optional)" : "Brand (optional)"}
                            </p>
                            <input className={inputCls} placeholder={isClass ? "Your studio name" : "Brother, Gildan, Xin Flying…"} value={form.brand} onChange={set("brand")} data-testid="sell-brand" />
                        </div>
                        <div>
                            <p className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Location</p>
                            <input className={inputCls} placeholder="Brooklyn, NY" value={form.location} onChange={set("location")} data-testid="sell-location" />
                        </div>
                        {isClass ? (
                            <div>
                                <p className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Seats per session</p>
                                <input className={inputCls} type="number" min="1" placeholder="3" value={form.capacity} onChange={set("capacity")} data-testid="sell-capacity" />
                            </div>
                        ) : (
                            <>
                                <div>
                                    <p className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Quantity</p>
                                    <input className={inputCls} type="number" min="1" value={form.quantity} onChange={set("quantity")} data-testid="sell-quantity" />
                                </div>
                                <div>
                                    <p className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Unit (roll, bag, piece…)</p>
                                    <input className={inputCls} placeholder="roll" value={form.unit} onChange={set("unit")} data-testid="sell-unit" />
                                </div>
                            </>
                        )}
                    </div>

                    <label className="flex items-center gap-2 text-sm text-zinc-300">
                        <input type="checkbox" checked={form.accept_offers} onChange={(e) => setForm((f) => ({ ...f, accept_offers: e.target.checked }))}
                            className="h-4 w-4 accent-[#2563EB]" data-testid="sell-offers" />
                        {isClass ? "Open to custom group pricing" : "Accept offers"}
                    </label>

                    <div>
                        <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Photos (up to 6)</p>
                        <div className="flex flex-wrap gap-3">
                            {form.images.map((img, i) => (
                                <div key={i} className="relative h-20 w-24 overflow-hidden rounded-xl border border-white/12">
                                    <img src={mediaUrl(img)} alt="" className="h-full w-full object-cover" />
                                    <button type="button" onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, x) => x !== i) }))}
                                        className="absolute right-1 top-1 rounded-full bg-black/80 p-1 text-red-400" data-testid={`sell-photo-remove-${i}`}><X size={11} /></button>
                                </div>
                            ))}
                            <label className="flex h-20 w-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-white/15 text-zinc-400 hover:border-brand-green/60" data-testid="sell-photo-add">
                                {uploading ? <Loader2 size={18} className="animate-spin text-brand-green" /> : <ImagePlus size={18} />}
                                <span className="font-mono text-[11px] uppercase tracking-widest">Add</span>
                                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addPhotos(e.target.files)} data-testid="sell-photo-input" />
                            </label>
                        </div>
                    </div>

                    <GreenButton className="w-full" onClick={submit} disabled={busy} data-testid="sell-submit">
                        {busy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Submit listing
                    </GreenButton>
                    <p className="text-[11px] text-zinc-500">
                        Basic seller plan — free, unlimited listings. A $5/month Pro plan with featured placement is coming once the marketplace gets busy.
                    </p>
                </div>
            </div>
        </Layout>
    );
}
